import { ChangeEvent, useEffect, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import {
  ApplicationCalendarMatchesTable,
  type NewScheduleWeek,
} from './components/ApplicationCalendarWeekScheduleTable/ApplicationCalendarWeekScheduleTable';
import {
  useScheduleWeek,
  usePostScheduleWeekMutation,
  useDeleteScheduleWeekMutation,
  usePutScheduleWeekMutation,
} from '@/services/queries/schedule-week';
import { breadcrumb } from './breadcrumb';
import { ScheduleWeek } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { queryClient } from '@/services/api/queryClient';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { useTranslation } from 'react-i18next';

export const ApplicationCalendarWeekSchedule = () => {
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedScheduleWeek, setSelectedScheduleWeek] = useState<ScheduleWeek>();
  const [newScheduleWeek, setNewScheduleWeek] = useState<NewScheduleWeek[]>([]);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    ApplicationComponentId.CALENDAR_WEEK_SCHEDULE,
  );
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);
  const { t } = useTranslation();

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_calendar_week.enabled')
      : t('pages.application_calendar_week.disabled');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_calendar_week.enabled')
      : t('pages.application_calendar_week.disabled');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.CALENDAR_WEEK_SCHEDULE, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: pendingAppAreaState
            ? t('pages.application_calendar_week.area_enabled')
            : t('pages.application_calendar_week.area_disabled'),
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: pendingAppAreaState
            ? t('pages.application_calendar_week.area_enabled')
            : t('pages.application_calendar_week.area_disabled'),
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const { data: weekSchedule, isLoading: isLoadingWeekSchedule } = useScheduleWeek();
  const { mutate: createWeekSchedule, isLoading: isLoadingCreateWeekSchedule } = usePostScheduleWeekMutation();
  const { mutate: updateWeekSchedule, isLoading: isLoadingUpdateWeekSchedule } = usePutScheduleWeekMutation();
  const { mutate: deleteWeekSchedule, isLoading: isLoadingDeleteWeekSchedule } = useDeleteScheduleWeekMutation();

  const toast = useToast();

  const removeNewScheduleByIndex = (index: number) => {
    setNewScheduleWeek(prev => {
      const copy = [...prev];

      copy.splice(index, 1);

      return copy;
    });
  };

  const handleSaveItem = (item: ScheduleWeek) => {
    updateWeekSchedule(
      {
        id: item.uuid,
        payload: {
          description: item.description,
        },
      },
      {
        onSuccess: async () => {
          toast({
            title: t('pages.application_calendar_week.save_success'),
            status: 'success',
          });

          await queryClient.invalidateQueries(['GET_SCHEDULE_WEEK']);
          await queryClient.refetchQueries(['GET_SCHEDULE_WEEK']);

          await new Promise(resolve => setTimeout(resolve, 500));
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_calendar_week.save_error'),
          });
        },
      },
    );
  };

  const handleDeleteItem = () => {
    if (!selectedScheduleWeek) return;

    deleteWeekSchedule(selectedScheduleWeek.uuid, {
      onSuccess: async () => {
        toast({
          title: t('pages.application_calendar_week.delete_success'),
          status: 'success',
        });

        await queryClient.invalidateQueries(['GET_SCHEDULE_WEEK']);
        await queryClient.refetchQueries(['GET_SCHEDULE_WEEK']);

        await new Promise(resolve => setTimeout(resolve, 500));

        setIsConfirmModalOpen(false);
        setSelectedScheduleWeek(undefined);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_calendar_week.delete_error'),
        });
      },
    });
  };

  const handleSaveNewItem = (newItem: NewScheduleWeek) => {
    if (!newItem.days_of_week || !newItem.description) {
      toast({
        title: t('pages.application_calendar_week.validation_error'),
        status: 'error',
      });

      return;
    }

    createWeekSchedule(newItem, {
      onSuccess: async () => {
        toast({
          title: t('pages.application_calendar_week.save_success'),
          status: 'success',
        });

        const scheduleWeekIndex = newScheduleWeek.findIndex(item => {
          return item.days_of_week === newItem.days_of_week && item.description === newItem.description;
        });

        if (scheduleWeekIndex) {
          removeNewScheduleByIndex(scheduleWeekIndex);
        }

        await queryClient.invalidateQueries(['GET_SCHEDULE_WEEK']);
        await queryClient.refetchQueries(['GET_SCHEDULE_WEEK']);

        await new Promise(resolve => setTimeout(resolve, 500));
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_calendar_week.save_error'),
        });
      },
    });
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_calendar_week.title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_calendar_week.section_title')}
                </Text>

                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive ? t('pages.application_calendar_week.enabled') : t('pages.application_calendar_week.disabled')}
                </Switch>
              </>
            )}
          </Flex>
        </Flex>

        <Button
          display='flex'
          alignItems='center'
          alignSelf='flex-start'
          gap={2}
          onClick={() => {
            setNewScheduleWeek(prev => {
              const copy = [...prev];

              copy.unshift({ days_of_week: '', description: '' });

              return copy;
            });
          }}
          width={{ base: '100%', md: 'initial' }}
        >
          <ChakraFeatherIcon name='plus-circle' />
          {t('pages.application_calendar_week.label')}
        </Button>

        <ApplicationCalendarMatchesTable
          isLoading={isLoadingWeekSchedule}
          items={weekSchedule ?? []}
          newItems={newScheduleWeek}
          onSaveClick={handleSaveItem}
          onDeleteClick={item => {
            setSelectedScheduleWeek(item);
            setIsConfirmModalOpen(true);
          }}
          onNewItemSaveClick={handleSaveNewItem}
          onNewItemDeleteClick={newItemIndex => removeNewScheduleByIndex(newItemIndex)}
        />
      </SectionBox>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedScheduleWeek(undefined);
        }}
        onConfirm={handleDeleteItem}
        isLoading={isLoadingDeleteWeekSchedule}
        title={t('pages.application_calendar_week.title_delete')}
        description={
          <Text>
            {t('pages.application_calendar_week.description')}
            <Text as='span' fontWeight='bold'>
              {selectedScheduleWeek?.description}
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_calendar_week.title_label')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_calendar_week.description_enabled')
              : t('pages.application_calendar_week.description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner
        isLoading={isLoadingEnableOrDisableAppArea || isLoadingCreateWeekSchedule || isLoadingUpdateWeekSchedule}
      />
    </PageContent>
  );
};
